import type { IAccountDataResponse, IGroup } from '@hit-game/shared/types';
import { normalizeSideConfigs } from '@hit-game/shared/utils';
import { AlertTriangle, Pencil, Save, Trash2, UserPlus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Dropdown } from '../common/Dropdown';
import { GroupMemberRow } from './GroupMemberRow';
import { GAME_BADGES, GAME_LABELS } from './group.constants';
import {
  draftSignature,
  toDraft,
  toMembers,
  validateDrafts,
  warnDrafts,
  type DraftMember,
} from './group.form';

// Bỏ cột "Side" và "Config": phe random lại mỗi ván, tiền khai ở cấp nhóm.
const COLUMNS = ['Tài khoản', 'Trạng thái', 'Proxy', 'Hành động'];

interface GroupPanelProps {
  group: IGroup;
  /** Toàn bộ tài khoản của user ở shape sảnh — có trạng thái chạy và proxy. */
  accounts: IAccountDataResponse[];
  /** MỌI nhóm của user — để loại tài khoản đã thuộc nhóm khác khỏi ô mời thêm. */
  allGroups: IGroup[];
  onSave: (group: IGroup, input: Partial<IGroup>) => Promise<boolean>;
  onEditConfig: (group: IGroup) => void;
  onDelete: (group: IGroup) => void;
}

/**
 * Thẻ một nhóm: bảng thành viên CHỈ ĐỌC, thêm/bớt tài khoản.
 *
 * Tiền cược cố tình không sửa được ở đây — nó nằm trong hộp thoại "Sửa nhóm" mở
 * bằng nút bút chì. Bảng này để trả lời "nhóm đang gồm ai, đăng nhập chưa, đi
 * proxy nào", nên nó phải đọc lướt được chứ không phải một cái form.
 */
export function GroupPanel({
  group,
  accounts,
  allGroups,
  onSave,
  onEditConfig,
  onDelete,
}: GroupPanelProps) {
  const [drafts, setDrafts] = useState<DraftMember[]>(() => toDraft(group.members));
  const [picked, setPicked] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const baseline = useMemo(
    () =>
      draftSignature({
        name: group.name,
        gameType: group.gameType,
        sideConfigs: group.sideConfigs,
        members: toDraft(group.members),
      }),
    [group],
  );

  // Trang tải lại sau mỗi lần lưu (và sau khi sửa nhóm khác), nên `group` là một
  // object mới mỗi vòng. Chỉ nạp lại bản nháp khi nội dung THẬT SỰ khác — nếu
  // không, mỗi lần tải lại là xoá trắng thứ người dùng đang xếp dở.
  useEffect(() => {
    setDrafts(toDraft(group.members));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- khoá theo NỘI DUNG nhóm, không theo định danh object
  }, [baseline]);

  const dirty =
    draftSignature({
      name: group.name,
      gameType: group.gameType,
      sideConfigs: group.sideConfigs,
      members: drafts,
    }) !== baseline;

  const accountById = useMemo(
    () => new Map(accounts.map((item) => [String(item._id), item])),
    [accounts],
  );

  /**
   * Chỉ mời tài khoản đang RẢNH: chưa có trong nhóm này (server từ chối trùng)
   * và chưa thuộc nhóm nào khác — một tài khoản nằm hai nhóm là hai nhóm cùng
   * sai khiến nó đặt lệnh, cửa nào cũng có thể chồng nhau.
   */
  const freeAccounts = useMemo(() => {
    const taken = new Set(drafts.map((draft) => draft.accountId));
    for (const other of allGroups) {
      if (String(other._id) === String(group._id)) continue;
      for (const member of other.members) taken.add(String(member.accountId));
    }

    return accounts
      .filter((account) => !taken.has(String(account._id)))
      .map((account) => ({ value: String(account._id), label: account.username }));
  }, [accounts, allGroups, drafts, group._id]);

  const warnings = warnDrafts(
    drafts,
    group.gameType,
    normalizeSideConfigs(group.sideConfigs, group.gameType),
  );
  const warning = warnings.length > 0 ? warnings.join(' ') : null;

  const addMember = () => {
    if (!picked) return;
    setDrafts((prev) => [...prev, { accountId: picked }]);
    setPicked('');
    setError(null);
  };

  const save = async () => {
    const invalid = validateDrafts(drafts);
    if (invalid) {
      setError(invalid);
      return;
    }

    setSaving(true);
    const ok = await onSave(group, { members: toMembers(drafts) });
    setSaving(false);

    if (!ok) setError('Lưu không thành công — kiểm tra lại rồi thử lại');
  };

  return (
    // KHÔNG `overflow-hidden` ở đây: bảng thành viên đã tự bo góc trong khung
    // riêng, còn cắt tràn ở thẻ thì menu của ô "Chọn tài khoản" bị xén mất.
    <div className="border-line bg-panel rounded-xl border p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="text-ink truncate text-[15px] font-bold">{group.name}</h3>
          <span
            className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
              GAME_BADGES[group.gameType] ?? 'bg-panel-hover text-ink-dim'
            }`}
            title={GAME_LABELS[group.gameType] ?? group.gameType}
          >
            {group.gameType}
          </span>
          <button
            type="button"
            onClick={() => {
              onEditConfig(group);
            }}
            // Bản nháp thành viên chưa lưu mà mở popup thì popup nạp từ bản CŨ
            // trên server, lưu xong là đè mất thứ đang xếp dở.
            disabled={dirty}
            title={
              dirty
                ? 'Lưu thay đổi thành viên trước rồi mới sửa cấu hình được'
                : 'Sửa nhóm — tên, game và mệnh giá cược'
            }
            className="text-ink-dim hover:bg-panel-hover hover:text-accent rounded p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Pencil size={13} />
          </button>
        </div>

        <button
          type="button"
          onClick={() => {
            onDelete(group);
          }}
          className="border-danger-border bg-danger-bg text-danger hover:bg-danger-bg-hover flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-bold transition-colors"
        >
          <Trash2 size={13} />
          Xoá nhóm
        </button>
      </div>

      {(error ?? warning) && (
        <div
          className={`mb-3 flex items-center gap-1.5 text-[11px] font-semibold ${
            error ? 'text-danger' : 'text-warning'
          }`}
        >
          <AlertTriangle size={13} className="shrink-0" />
          <span>{error ?? warning}</span>
        </div>
      )}

      <div className="border-line overflow-x-auto rounded-lg border">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-canvas-raised">
              {COLUMNS.map((column) => (
                <th
                  key={column}
                  className={`text-ink-faint px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] ${
                    column === 'Hành động' ? 'text-center' : ''
                  }`}
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {drafts.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="text-ink-faint px-4 py-8 text-center text-[12px]"
                >
                  Chưa có thành viên nào trong nhóm
                </td>
              </tr>
            ) : (
              drafts.map((draft, index) => (
                // Không có id ổn định cho dòng chưa chọn tài khoản, nên vẫn khoá
                // theo vị trí — dòng chỉ thêm ở cuối hoặc xoá tại chỗ, không sắp
                // xếp lại, nên `index` không trỏ nhầm dòng.
                <GroupMemberRow
                  key={index}
                  draft={draft}
                  index={index}
                  account={accountById.get(draft.accountId)}
                  onRemove={() => {
                    setDrafts((prev) => prev.filter((_, i) => i !== index));
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="border-line mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-dashed pt-4">
        <div className="flex items-center gap-2">
          <Dropdown
            className="w-56"
            value={picked}
            onChange={setPicked}
            options={freeAccounts}
            placeholder={
              freeAccounts.length > 0 ? '-- Chọn tài khoản --' : '-- Không có tài khoản rảnh --'
            }
            disabled={freeAccounts.length === 0 || saving}
          />
          <button
            type="button"
            onClick={addMember}
            disabled={!picked || saving}
            className="bg-accent hover:bg-accent-hover disabled:bg-panel-hover disabled:text-ink-dim flex items-center gap-1.5 whitespace-nowrap rounded-lg px-4 py-2 text-[12px] font-bold text-white transition-colors disabled:cursor-not-allowed"
          >
            <UserPlus size={14} />
            Thêm tài khoản
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              void save();
            }}
            // Không có gì đổi thì bấm Lưu chỉ tốn một request ghi đè y hệt.
            disabled={!dirty || saving}
            title={dirty ? 'Lưu thay đổi' : 'Chưa có thay đổi nào'}
            className="bg-accent hover:bg-accent-hover disabled:bg-panel-hover disabled:text-ink-dim ml-1 flex items-center gap-1.5 whitespace-nowrap rounded-lg px-4 py-2 text-[12px] font-bold text-white transition-colors disabled:cursor-not-allowed"
          >
            <Save size={14} className={saving ? 'animate-pulse' : ''} />
            {saving ? 'Đang lưu...' : 'Lưu'}
          </button>
        </div>
      </div>
    </div>
  );
}
